import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Plus, X, Paperclip, Download, Trash2, Send, User, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const TicketsList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    attachments: []
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingTicketAttachment, setUploadingTicketAttachment] = useState(false);
  const [statusFilter, setStatusFilter] = useState('NOWE');
  const [pastedImages, setPastedImages] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState({});
  const [commentText, setCommentText] = useState('');
  const [pastedCommentImages, setPastedCommentImages] = useState([]);
  const [commentAttachmentUrls, setCommentAttachmentUrls] = useState({});
  const commentAttachmentUrlsRef = useRef({});
  const attachmentUrlsRef = useRef({});
  const { user } = useAuth();

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pobierz blob URL dla obrazów załączników
  useEffect(() => {
    if (!selectedTicket || !selectedTicket.attachments) {
      Object.values(attachmentUrlsRef.current).forEach(url => {
        if (url) window.URL.revokeObjectURL(url);
      });
      attachmentUrlsRef.current = {};
      setAttachmentUrls({});
      return;
    }

    const loadAttachmentUrls = async () => {
      Object.values(attachmentUrlsRef.current).forEach(url => {
        if (url) window.URL.revokeObjectURL(url);
      });

      const urls = {};
      for (const attachment of selectedTicket.attachments) {
        const isImage = attachment.fileType?.startsWith('image/') || 
                       attachment.originalFileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        if (isImage) {
          try {
            const response = await api.get(`/tickets/${selectedTicket.id}/attachments/${attachment.id}/download`, {
              responseType: 'blob'
            });
            const blob = new Blob([response.data], { 
              type: response.headers['content-type'] || 'image/png' 
            });
            const url = window.URL.createObjectURL(blob);
            urls[attachment.id] = url;
          } catch (error) {
            console.error('Error loading attachment image:', error);
          }
        }
      }
      attachmentUrlsRef.current = urls;
      setAttachmentUrls(urls);
    };

    loadAttachmentUrls();

    return () => {
      Object.values(attachmentUrlsRef.current).forEach(url => {
        if (url) window.URL.revokeObjectURL(url);
      });
      attachmentUrlsRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket?.id, selectedTicket?.attachments?.length]);

  // Pobierz blob URL dla obrazów załączników komentarzy
  useEffect(() => {
    if (!selectedTicket || !selectedTicket.comments) {
      Object.values(commentAttachmentUrlsRef.current).forEach(url => {
        if (url) window.URL.revokeObjectURL(url);
      });
      commentAttachmentUrlsRef.current = {};
      setCommentAttachmentUrls({});
      return;
    }

    const loadCommentAttachmentUrls = async () => {
      Object.values(commentAttachmentUrlsRef.current).forEach(url => {
        if (url) window.URL.revokeObjectURL(url);
      });

      const urls = {};
      for (const comment of selectedTicket.comments || []) {
        for (const attachment of comment.attachments || []) {
          const isImage = attachment.fileType?.startsWith('image/') || 
                         attachment.originalFileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
          if (isImage) {
            try {
              const response = await api.get(`/tickets/${selectedTicket.id}/comments/${comment.id}/attachments/${attachment.id}/download`, {
                responseType: 'blob'
              });
              const blob = new Blob([response.data], { 
                type: response.headers['content-type'] || 'image/png' 
              });
              const url = window.URL.createObjectURL(blob);
              urls[`${comment.id}_${attachment.id}`] = url;
            } catch (error) {
              console.error('Error loading comment attachment image:', error);
            }
          }
        }
      }
      commentAttachmentUrlsRef.current = urls;
      setCommentAttachmentUrls(urls);
    };

    loadCommentAttachmentUrls();

    return () => {
      Object.values(commentAttachmentUrlsRef.current).forEach(url => {
        if (url) window.URL.revokeObjectURL(url);
      });
      commentAttachmentUrlsRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket?.id, selectedTicket?.comments]);

  // Obsługa wklejania obrazów ze schowka w komentarzach
  useEffect(() => {
    const handlePaste = async (e) => {
      if (!selectedTicket || showCreateModal) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          
          const blob = item.getAsFile();
          if (!blob) continue;

          const timestamp = new Date().getTime();
          const fileName = `screenshot-${timestamp}.png`;
          const file = new File([blob], fileName, { type: 'image/png' });
          
          const reader = new FileReader();
          reader.onload = (event) => {
            setPastedCommentImages(prev => [...prev, {
              name: fileName,
              preview: event.target.result,
              file: file
            }]);
          };
          reader.readAsDataURL(file);

          toast.success('Screenshot został wklejony');
          break;
        }
      }
    };

    if (selectedTicket && !showCreateModal) {
      window.addEventListener('paste', handlePaste);
      return () => {
        window.removeEventListener('paste', handlePaste);
      };
    }
  }, [selectedTicket, showCreateModal]);

  // Obsługa wklejania obrazów w formularzu tworzenia
  useEffect(() => {
    const handlePaste = async (e) => {
      if (!showCreateModal) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          
          const blob = item.getAsFile();
          if (!blob) continue;

          const timestamp = new Date().getTime();
          const fileName = `screenshot-${timestamp}.png`;
          const file = new File([blob], fileName, { type: 'image/png' });
          
          setNewTicket(prev => ({
            ...prev,
            attachments: [...prev.attachments, {
              file: file,
              name: fileName,
              size: file.size,
              isPasted: true
            }]
          }));

          const reader = new FileReader();
          reader.onload = (event) => {
            setPastedImages(prev => [...prev, {
              name: fileName,
              preview: event.target.result,
              file: file
            }]);
          };
          reader.readAsDataURL(file);

          toast.success('Screenshot został wklejony');
          break;
        }
      }
    };

    if (showCreateModal) {
      window.addEventListener('paste', handlePaste);
      return () => {
        window.removeEventListener('paste', handlePaste);
      };
    }
  }, [showCreateModal]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tickets');
      setTickets(response.data);
      if (response.data.length > 0 && !selectedTicket) {
        setSelectedTicket(response.data[0]);
      }
    } catch (error) {
      toast.error('Błąd podczas pobierania zgłoszeń');
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedTicket = async (ticketId) => {
    const updatedResponse = await api.get('/tickets');
    setTickets(updatedResponse.data);
    const updatedTicket = updatedResponse.data.find(t => t.id === ticketId);
    if (updatedTicket) {
      setSelectedTicket(updatedTicket);
    }
    return updatedTicket;
  };

  const uploadFilesToTicket = async (ticketId, files) => {
    if (!ticketId || !files || files.length === 0) return;

    setUploadingTicketAttachment(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/tickets/${ticketId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await refreshSelectedTicket(ticketId);
      toast.success(files.length === 1 ? 'Załącznik został dodany' : `Dodano ${files.length} załączników`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas dodawania załącznika');
      console.error('Error uploading ticket attachment:', error);
    } finally {
      setUploadingTicketAttachment(false);
    }
  };

  const handleTicketAttachmentChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !selectedTicket) return;
    await uploadFilesToTicket(selectedTicket.id, files);
    e.target.value = '';
  };

  const handleTicketAttachmentPaste = async (e) => {
    if (!selectedTicket) return;
    e.stopPropagation();
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (!blob) continue;
        const fileName = `screenshot-${Date.now()}.png`;
        const file = new File([blob], fileName, { type: blob.type || 'image/png' });
        await uploadFilesToTicket(selectedTicket.id, [file]);
        break;
      }
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFile(true);
    const uploadedFiles = [];

    try {
      for (const file of files) {
        uploadedFiles.push({
          file: file,
          name: file.name,
          size: file.size
        });
      }
      
      setNewTicket(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));
    } catch (error) {
      toast.error('Błąd podczas przetwarzania plików');
      console.error('Error processing files:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const removeAttachment = (index) => {
    setNewTicket(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    setPastedImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeCommentImage = (index) => {
    setPastedCommentImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) {
      toast.error('Tytuł i opis są wymagane');
      return;
    }

    try {
      setLoading(true);
      
      const ticketResponse = await api.post('/tickets', {
        title: newTicket.title,
        description: newTicket.description
      });

      const ticketId = ticketResponse.data.id;

      if (newTicket.attachments.length > 0) {
        for (const attachment of newTicket.attachments) {
          const formData = new FormData();
          formData.append('file', attachment.file);
          
          await api.post(`/tickets/${ticketId}/attachments`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        }
      }

      toast.success('Zgłoszenie zostało utworzone');
      setShowCreateModal(false);
      setNewTicket({ title: '', description: '', attachments: [] });
      setPastedImages([]);
      await fetchTickets();
      const updatedResponse = await api.get('/tickets');
      const newTicketFromList = updatedResponse.data.find(t => t.id === ticketId);
      if (newTicketFromList) {
        setSelectedTicket(newTicketFromList);
      }
    } catch (error) {
      toast.error('Błąd podczas tworzenia zgłoszenia');
      console.error('Error creating ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    if (!isSuperAdmin) return;

    try {
      await api.patch(`/tickets/${ticketId}/status`, { status: newStatus });
      toast.success('Status zgłoszenia został zaktualizowany');
      await fetchTickets();
      const updatedResponse = await api.get('/tickets');
      const updatedTicket = updatedResponse.data.find(t => t.id === ticketId);
      if (updatedTicket) {
        setSelectedTicket(updatedTicket);
      }
    } catch (error) {
      toast.error('Błąd podczas zmiany statusu');
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!isSuperAdmin) return;
    if (!window.confirm('Czy na pewno chcesz usunąć to zgłoszenie?')) return;

    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success('Zgłoszenie zostało usunięte');
      setSelectedTicket(null);
      await fetchTickets();
    } catch (error) {
      toast.error('Błąd podczas usuwania zgłoszenia');
      console.error('Error deleting ticket:', error);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() && pastedCommentImages.length === 0) {
      toast.error('Komentarz nie może być pusty');
      return;
    }

    if (!selectedTicket) return;

    try {
      const formData = new FormData();
      formData.append('content', commentText.trim());
      
      // Dodaj wklejone obrazy jako pliki
      for (const img of pastedCommentImages) {
        formData.append('file', img.file);
      }

      await api.post(`/tickets/${selectedTicket.id}/comments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Komentarz został dodany');
      setCommentText('');
      setPastedCommentImages([]);
      
      await refreshSelectedTicket(selectedTicket.id);
    } catch (error) {
      toast.error('Błąd podczas dodawania komentarza');
      console.error('Error adding comment:', error);
    }
  };

  const downloadAttachment = async (ticketId, attachmentId, fileName) => {
    try {
      const response = await api.get(`/tickets/${ticketId}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Błąd podczas pobierania pliku');
      console.error('Error downloading attachment:', error);
    }
  };

  const downloadCommentAttachment = async (ticketId, commentId, attachmentId, fileName) => {
    try {
      const response = await api.get(`/tickets/${ticketId}/comments/${commentId}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Błąd podczas pobierania pliku');
      console.error('Error downloading comment attachment:', error);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'NOWE': 'Nowy',
      'W_TRAKCIE': 'W trakcie',
      'ZREALIZOWANE': 'Rozwiązany',
      'ZAKONCZONE': 'Zamknięty'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'NOWE': 'bg-blue-500 text-white',
      'W_TRAKCIE': 'bg-yellow-500 text-white',
      'ZREALIZOWANE': 'bg-green-500 text-white',
      'ZAKONCZONE': 'bg-gray-500 text-white'
    };
    return colors[status] || 'bg-gray-500 text-white';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusCounts = () => {
    const counts = {
      'ALL': tickets.length,
      'NOWE': tickets.filter(t => t.status === 'NOWE').length,
      'W_TRAKCIE': tickets.filter(t => t.status === 'W_TRAKCIE').length,
      'ZREALIZOWANE': tickets.filter(t => t.status === 'ZREALIZOWANE').length,
      'ZAKONCZONE': tickets.filter(t => t.status === 'ZAKONCZONE').length
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  const filteredTickets = statusFilter && statusFilter !== 'ALL'
    ? tickets.filter(ticket => ticket.status === statusFilter)
    : tickets;

  const getDescriptionPreview = (description, maxLength = 100) => {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + '...';
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50" style={{ height: 'calc(100vh - 4rem)' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Zgłoszenia</h1>
            <p className="text-sm text-gray-600 mt-1">Zgłaszaj problemy i pytania</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nowe zgłoszenie
          </button>
        </div>
      </div>

      {/* Main Content - Master-Detail Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - List */}
        <div className="w-full lg:w-1/3 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
          <div className="p-5 bg-white border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">
              {isSuperAdmin ? 'Wszystkie zgłoszenia' : 'Moje zgłoszenia'}
            </h2>
            
            {/* Status Filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('ALL')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'ALL' || !statusFilter
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Wszystkie ({statusCounts.ALL})
              </button>
              <button
                onClick={() => setStatusFilter('NOWE')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'NOWE'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Nowy ({statusCounts.NOWE})
              </button>
              <button
                onClick={() => setStatusFilter('W_TRAKCIE')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'W_TRAKCIE'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                W trakcie ({statusCounts.W_TRAKCIE})
              </button>
              <button
                onClick={() => setStatusFilter('ZREALIZOWANE')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'ZREALIZOWANE'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Rozwiązany ({statusCounts.ZREALIZOWANE})
              </button>
              <button
                onClick={() => setStatusFilter('ZAKONCZONE')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'ZAKONCZONE'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Zamknięty ({statusCounts.ZAKONCZONE})
              </button>
            </div>
          </div>

          {/* Tickets List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {filteredTickets.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Brak zgłoszeń</h3>
                <p className="mt-1 text-sm text-gray-500">Rozpocznij od utworzenia pierwszego zgłoszenia.</p>
              </div>
            ) : (
              filteredTickets.map((ticket) => {
                const isSelected = selectedTicket?.id === ticket.id;
                return (
                  <div
                    key={ticket.id}
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setShowDetails(true);
                    }}
                    className={`p-4 cursor-pointer transition-all rounded-lg border ${
                      isSelected
                        ? 'bg-primary-50 border-primary-200 shadow-sm'
                        : 'bg-white border-gray-200 hover:shadow-md hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900 flex-1 pr-2">
                        {ticket.title}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)} flex-shrink-0`}>
                        {getStatusLabel(ticket.status)}
                      </span>
                    </div>
                    
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                      {getDescriptionPreview(ticket.description)}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {ticket.attachments && ticket.attachments.length > 0 && (
                          <div className="flex items-center gap-1">
                            <ImageIcon className="h-3 w-3" />
                            <span>{ticket.attachments.length} screenshot{ticket.attachments.length !== 1 ? 'i' : ''}</span>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(ticket.createdAt)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel - Details */}
        <div className={`${showDetails ? 'flex' : 'hidden'} lg:flex flex-1 bg-white overflow-y-auto flex-col`}>
          {selectedTicket ? (
            <div className="p-6 flex-1">
              {/* Back button for mobile */}
              <button
                onClick={() => setShowDetails(false)}
                className="lg:hidden mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900"
              >
                <X className="h-4 w-4 mr-1" />
                Powrót do listy
              </button>
              
              {/* Header with title and status */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">{selectedTicket.title}</h2>
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedTicket.status)}`}>
                        {getStatusLabel(selectedTicket.status)}
                      </span>
                      {isSuperAdmin && (
                        <button
                          onClick={() => handleDeleteTicket(selectedTicket.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Usuń zgłoszenie"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Data utworzenia:</span>{' '}
                    <span>{formatDateTime(selectedTicket.createdAt)}</span>
                  </div>
                  <div>
                    <span className="font-medium">Utworzone przez:</span>{' '}
                    <span>{selectedTicket.userFirstName} {selectedTicket.userLastName}</span>
                  </div>
                </div>

                {/* Status change dropdown - only for super admin */}
                {isSuperAdmin && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Zmień status:</label>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="NOWE">Nowy</option>
                      <option value="W_TRAKCIE">W trakcie</option>
                      <option value="ZREALIZOWANE">Rozwiązany</option>
                      <option value="ZAKONCZONE">Zamknięty</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Opis</h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>
              </div>

              {/* Attachments */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Załączniki ({selectedTicket.attachments?.length || 0})
                  </h3>
                  <label className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 cursor-pointer ${uploadingTicketAttachment ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Paperclip className="h-3.5 w-3.5" />
                    {uploadingTicketAttachment ? 'Dodawanie...' : 'Dodaj plik'}
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleTicketAttachmentChange}
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                    />
                  </label>
                </div>

                <div
                  tabIndex={0}
                  onPaste={handleTicketAttachmentPaste}
                  className="mb-4 p-3 bg-blue-50 border border-blue-200 border-dashed rounded-lg text-xs text-blue-800 outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Możesz dodać plik przyciskiem „Dodaj plik” albo wkleić screenshot tutaj (<kbd className="px-1 py-0.5 bg-blue-100 rounded font-mono">Ctrl+V</kbd>).
                  Załączniki można później pobrać przyciskiem „Pobierz”.
                </div>

                {selectedTicket.attachments && selectedTicket.attachments.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {selectedTicket.attachments.map((attachment) => {
                      const isImage = attachment.fileType?.startsWith('image/') || 
                                     attachment.originalFileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                      return (
                        <div
                          key={attachment.id}
                          className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white"
                        >
                          {isImage && attachmentUrls[attachment.id] ? (
                            <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                              <img
                                src={attachmentUrls[attachment.id]}
                                alt={attachment.originalFileName}
                                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => downloadAttachment(selectedTicket.id, attachment.id, attachment.originalFileName)}
                              />
                            </div>
                          ) : (
                            <div className="aspect-square bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                                 onClick={() => downloadAttachment(selectedTicket.id, attachment.id, attachment.originalFileName)}
                            >
                              <Paperclip className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                          <div className="p-2 bg-white">
                            <p className="text-xs text-gray-700 truncate" title={attachment.originalFileName}>
                              {attachment.originalFileName}
                            </p>
                            <button
                              onClick={() => downloadAttachment(selectedTicket.id, attachment.id, attachment.originalFileName)}
                              className="mt-1 text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                            >
                              <Download className="h-3 w-3" />
                              Pobierz
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    Brak załączników. Dodaj plik lub wklej screenshot powyżej.
                  </div>
                )}
              </div>

              {/* Comments Section */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  Komentarze ({selectedTicket.comments?.length || 0})
                </h3>
                
                {/* Comments list */}
                {selectedTicket.comments && selectedTicket.comments.length > 0 && (
                  <div className="space-y-4 mb-6">
                    {selectedTicket.comments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-900">
                              {comment.userFirstName} {comment.userLastName}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDateTime(comment.createdAt)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">{comment.content}</p>
                        
                        {/* Comment attachments */}
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div className="flex gap-2 flex-wrap mt-2">
                            {comment.attachments.map((attachment) => {
                              const isImage = attachment.fileType?.startsWith('image/') || 
                                           attachment.originalFileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                              const imageUrl = commentAttachmentUrls[`${comment.id}_${attachment.id}`];
                              return (
                                <div key={attachment.id} className="relative">
                                  {isImage && imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={attachment.originalFileName}
                                      className="w-20 h-20 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-90"
                                      onClick={() => downloadCommentAttachment(selectedTicket.id, comment.id, attachment.id, attachment.originalFileName)}
                                    />
                                  ) : (
                                    <div 
                                      className="w-20 h-20 bg-gray-200 rounded border border-gray-300 flex items-center justify-center cursor-pointer hover:bg-gray-300"
                                      onClick={() => downloadCommentAttachment(selectedTicket.id, comment.id, attachment.id, attachment.originalFileName)}
                                    >
                                      <Paperclip className="h-6 w-6 text-gray-500" />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Comment input */}
                <div className="space-y-3">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Dodaj komentarz... (możesz wkleić screenshot: Ctrl+V)"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  
                  {/* Pasted images preview */}
                  {pastedCommentImages.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {pastedCommentImages.map((img, index) => (
                        <div key={index} className="relative">
                          {img.preview ? (
                            <img
                              src={img.preview}
                              alt={img.name}
                              className="w-20 h-20 object-cover rounded border border-gray-200"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded border border-gray-200 bg-gray-50 flex flex-col items-center justify-center p-1">
                              <Paperclip className="h-5 w-5 text-gray-500" />
                              <span className="text-[10px] text-gray-600 truncate w-full text-center mt-1">{img.name}</span>
                            </div>
                          )}
                          <button
                            onClick={() => removeCommentImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3">
                  <label htmlFor="commentAttachments" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 cursor-pointer">
                    <Paperclip className="h-3.5 w-3.5" />
                    Dołącz plik do komentarza
                    <input
                      id="commentAttachments"
                      type="file"
                      multiple
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        Promise.all(files.map(file => new Promise((resolve) => {
                          if (file.type.startsWith('image/')) {
                            const reader = new FileReader();
                            reader.onload = (ev) => resolve({
                              name: file.name,
                              preview: ev.target.result,
                              file,
                            });
                            reader.readAsDataURL(file);
                          } else {
                            resolve({ name: file.name, preview: null, file });
                          }
                        }))).then((items) => {
                          setPastedCommentImages(prev => [...prev, ...items]);
                        });
                        e.target.value = '';
                      }}
                    />
                  </label>
                  
                  <button
                    onClick={handleAddComment}
                    disabled={(!commentText.trim() && pastedCommentImages.length === 0) || loading}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span>Dodawanie...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Dodaj komentarz
                      </>
                    )}
                  </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MessageSquare className="mx-auto h-16 w-16 text-gray-300" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">Wybierz zgłoszenie z listy</h3>
                <p className="mt-2 text-sm text-gray-500">Wybierz zgłoszenie z listy, aby zobaczyć szczegóły</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal tworzenia zgłoszenia */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Nowe zgłoszenie</h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewTicket({ title: '', description: '', attachments: [] });
                    setPastedImages([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="ticketTitle" className="block text-sm font-medium text-gray-700 mb-2">
                    Tytuł <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="ticketTitle"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Krótki opis problemu"
                  />
                </div>

                <div>
                  <label htmlFor="ticketDescription" className="block text-sm font-medium text-gray-700 mb-2">
                    Opis <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="ticketDescription"
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Szczegółowy opis problemu..."
                  />
                </div>

                <div>
                  <label htmlFor="ticketAttachments" className="block text-sm font-medium text-gray-700 mb-2">
                    Załączniki (opcjonalnie)
                  </label>
                  <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs text-blue-700">
                      💡 <strong>Wskazówka:</strong> Możesz wkleić screenshot bezpośrednio tutaj używając <kbd className="px-1.5 py-0.5 bg-blue-100 rounded text-xs font-mono">Ctrl+V</kbd> lub <kbd className="px-1.5 py-0.5 bg-blue-100 rounded text-xs font-mono">Cmd+V</kbd>
                    </p>
                  </div>
                  <input
                    type="file"
                    id="ticketAttachments"
                    multiple
                    onChange={handleFileChange}
                    accept="image/*"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  {newTicket.attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {newTicket.attachments.map((attachment, index) => {
                        const pastedImage = pastedImages.find(img => img.name === attachment.name);
                        return (
                          <div key={index} className="border border-gray-200 rounded-md overflow-hidden">
                            <div className="flex items-center justify-between p-2 bg-gray-50">
                              <div className="flex items-center gap-2">
                                {attachment.isPasted && (
                                  <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded">
                                    Wklejony
                                  </span>
                                )}
                                <span className="text-sm text-gray-700">{attachment.name}</span>
                                <span className="text-xs text-gray-500">({formatFileSize(attachment.size)})</span>
                              </div>
                              <button
                                onClick={() => removeAttachment(index)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            {pastedImage && pastedImage.preview && (
                              <div className="p-2 bg-white border-t border-gray-200">
                                <img
                                  src={pastedImage.preview}
                                  alt={attachment.name}
                                  className="max-w-full h-auto max-h-48 rounded border border-gray-200"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewTicket({ title: '', description: '', attachments: [] });
                      setPastedImages([]);
                    }}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleCreateTicket}
                    disabled={loading || uploadingFile}
                    className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading || uploadingFile ? (
                      <span className="flex items-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Tworzenie...</span>
                      </span>
                    ) : (
                      'Utwórz zgłoszenie'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsList;
